<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="title"
    size="xl"
    hide-footer
    body-class="sim-modal-body"
    modal-class="sim-modal"
  >
    <div class="sim-page-root">
      <div class="sim-inner">
        <div class="controls-row" :class="{ 'fade-up': mountedAnimated }">
          <!-- Numerador -->
          <div class="slider-card" :class="{ 'slider-pulse': pulseNumerador }">
            <div class="float-anchor">
              <transition-group name="float-proc">
                <div
                  v-for="item in floatsNumerador"
                  :key="item.id"
                  class="float-proc"
                  :style="{ '--ox': item.offsetX + 'px' }"
                >
                  <span class="float-proc-icon" v-if="item.proc.icon != null">{{
                    item.proc.icon
                  }}</span>
                  <div class="float-proc-body">
                    <code
                      v-if="item.proc.codigo != null"
                      class="float-proc-code"
                      >{{ item.proc.codigo }}</code
                    >
                    <span class="float-proc-desc">{{ item.proc.desc }}</span>
                  </div>
                  <span class="float-proc-check">✓</span>
                </div>
              </transition-group>
            </div>
            <div class="slider-card__header">
              <div class="slider-card__title-wrap">
                <span class="slider-card__icon red">NM</span>
                <span class="slider-card__title">{{ numeradorLabel }}</span>
              </div>

              <span
                v-if="deltaNumerador !== 0"
                class="delta-badge"
                :class="{
                  'delta-badge--pos': deltaNumerador > 0,
                  'delta-badge--neg': deltaNumerador < 0,
                }"
              >
                {{ formatDeltaFn(deltaNumerador) }}
              </span>
            </div>

            <div class="slider-wrap">
              <div class="slider-track"></div>
              <div
                class="slider-fill"
                :style="{
                  width: sliderPercent(valorNumerador, numeradorMin) + '%',
                }"
              ></div>

              <b-form-input
                class="slider-input"
                type="range"
                :min="numeradorMin"
                :max="maxValor"
                :step="numeradorStep"
                :value="valorNumerador"
                @input="onInputNumerador"
              />
            </div>

            <b-form-input
              type="number"
              :min="numeradorMin"
              :max="maxValor"
              :step="numeradorStep"
              class="number-input"
              :value="valorNumerador"
              @input="onInputNumerador"
            />
          </div>

          <!-- Denominador -->
          <div
            class="slider-card"
            :class="{ 'slider-pulse': pulseDenominador }"
          >
            <div class="float-anchor">
              <transition-group name="float-proc">
                <div
                  v-for="item in floatsDenominador"
                  :key="item.id"
                  class="float-proc float-proc--denom"
                  :style="{ '--ox': item.offsetX + 'px' }"
                >
                  <span class="float-proc-icon" v-if="item.proc.icon != null">{{
                    item.proc.icon
                  }}</span>
                  <div class="float-proc-body">
                    <code
                      v-if="item.proc.codigo != null"
                      class="float-proc-code float-proc-code--denom"
                    >
                      {{ item.proc.codigo }}
                    </code>
                    <span class="float-proc-desc">{{ item.proc.desc }}</span>
                  </div>
                  <span class="float-proc-check float-proc-check--denom"
                    >✓</span
                  >
                </div>
              </transition-group>
            </div>
            <div class="slider-card__header">
              <div class="slider-card__title-wrap">
                <span class="slider-card__icon">DN</span>
                <span class="slider-card__title">{{ denominadorLabel }}</span>
              </div>

              <span
                v-if="deltaDenominador !== 0"
                class="delta-badge"
                :class="{
                  'delta-badge--pos': deltaDenominador > 0,
                  'delta-badge--neg': deltaDenominador < 0,
                }"
              >
                {{ formatDeltaFn(deltaDenominador) }}
              </span>
            </div>

            <div class="slider-wrap">
              <div class="slider-track"></div>
              <div
                class="slider-fill"
                :style="{
                  width: sliderPercent(valorDenominador, denominadorMin) + '%',
                }"
              ></div>

              <b-form-input
                class="slider-input"
                type="range"
                :min="denominadorMin"
                :max="maxValor"
                :value="valorDenominador"
                @input="onInputDenominador"
              />
            </div>

            <b-form-input
              type="number"
              :min="denominadorMin"
              :max="maxValor"
              class="number-input"
              :value="valorDenominador"
              @input="onInputDenominador"
            />
          </div>

          <!-- Resultado -->
          <div class="result-card">
            <div class="gauge-ring" :style="gaugeStyle">
              <div class="gauge-ring__inner">
                <div class="gauge-ring__value">{{ resultadoFormatado }}</div>
                <div class="gauge-ring__label">{{ classeCalculada.nivel }}</div>
              </div>
            </div>

            <div
              v-if="
                card != 'desenvolvimento-infantil' &&
                card != 'gestantes-puerperas' &&
                card != 'pessoa-diabetes' &&
                card != 'pessoa-hipertensao' &&
                card != 'pessoa-idosa' &&
                card != 'saude-mulher'
              "
              class="result-pill"
              :class="resultPillClass"
            >
              {{
                resultTextFn({
                  numerador: valorNumerador,
                  denominador: valorDenominador,
                  resultado: resultadoCalculado,
                  classe: classeCalculada,
                })
              }}
            </div>
          </div>
        </div>

        <!-- Classificação -->
        <div class="section-card fade-up fade-up-1" v-if="mountedAnimated">
          <div class="section-title">CLASSIFICAÇÃO</div>

          <div class="chips-row">
            <button
              v-for="tipo in tipos"
              :key="tipo.value"
              type="button"
              class="chip"
              :class="[
                chipClass(tipo.value),
                { 'chip-active': classeCalculada.nivel === tipo.value },
              ]"
              @click="onAplicarPreset(tipo.value)"
            >
              <span class="chip__title">{{ tipo.label }}</span>
              <span class="chip__sub">{{ tipo.description }}</span>
            </button>
          </div>

          <p v-if="hint" class="section-hint">
            {{ hint }}
          </p>
        </div>

        <!-- Procedimentos -->
        <div
          class="procedures-grid"
          :class="{ 'fade-up fade-up-2': mountedAnimated }"
        >
          <div class="proc-table">
            <div class="proc-header">
              <span class="proc-header__icon red">NM</span>
              <span class="proc-header__title">{{ numeradorLabel }}</span>
              <span class="proc-header__count">{{
                procedimentosNumerador.length
              }}</span>
            </div>

            <div class="proc-body">
              <div
                v-for="p in procedimentosVisiveis(
                  procedimentosNumerador,
                  'numerador'
                )"
                :key="p.codigo + p.desc"
                class="proc-row"
              >
                <code
                  v-if="p.codigo != null"
                  class="proc-code proc-code--red"
                  >{{ p.codigo }}</code
                >
                <span class="proc-desc">{{ p.desc }}</span>
              </div>

              <button
                v-if="procedimentosNumerador.length > limiteProcedimentos"
                type="button"
                class="proc-toggle"
                @click="toggleProcedimentos('numerador')"
              >
                {{
                  expandidoNumerador
                    ? 'Mostrar menos ▲'
                    : `Ver todos os ${procedimentosNumerador.length} procedimentos ▼`
                }}
              </button>
            </div>
          </div>

          <div class="proc-table">
            <div class="proc-header">
              <span class="proc-header__icon">DN</span>
              <span class="proc-header__title">{{ denominadorLabel }}</span>
              <span class="proc-header__count">{{
                procedimentosDenominador.length
              }}</span>
            </div>

            <div class="proc-body">
              <div
                v-for="p in procedimentosVisiveis(
                  procedimentosDenominador,
                  'denominador'
                )"
                :key="p.codigo + p.desc"
                class="proc-row"
              >
                <code
                  v-if="p.codigo != null"
                  class="proc-code proc-code--blue"
                  >{{ p.codigo }}</code
                >
                <span class="proc-desc">{{ p.desc }}</span>
              </div>

              <button
                v-if="procedimentosDenominador.length > limiteProcedimentos"
                type="button"
                class="proc-toggle"
                @click="toggleProcedimentos('denominador')"
              >
                {{
                  expandidoDenominador
                    ? 'Mostrar menos ▲'
                    : `Ver todos os ${procedimentosDenominador.length} procedimentos ▼`
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'BaseSimuladorIndicadorModal',
  props: {
    modalId: { type: String, default: 'base-simulador-indicador-modal' },
    title: { type: String, default: 'Simulador' },

    card: {
      type: String,
      default: '',
    },

    numeradorLabel: { type: String, default: 'Numerador' },
    denominadorLabel: { type: String, default: 'Denominador' },

    valorNumerador: { type: Number, default: 0 },
    valorDenominador: { type: Number, default: 1 },

    baseNumerador: { type: Number, default: 0 },
    baseDenominador: { type: Number, default: 1 },

    numeradorMin: { type: Number, default: 0 },
    denominadorMin: { type: Number, default: 1 },
    maxValor: { type: Number, default: 1000000 },

    procedimentosNumerador: {
      type: Array,
      default: () => [],
    },
    procedimentosDenominador: {
      type: Array,
      default: () => [],
    },

    tipos: {
      type: Array,
      default: () => [],
    },

    hint: {
      type: String,
      default: '',
    },

    calculateResultFn: {
      type: Function,
      required: true,
    },

    calculateClassFn: {
      type: Function,
      required: true,
    },

    applyPresetFn: {
      type: Function,
      required: true,
    },

    formatDeltaFn: {
      type: Function,
      default: (val) => {
        if (val > 0) return `+${val}`
        if (val < 0) return `${val}`
        return ''
      },
    },

    resultTextFn: {
      type: Function,
      default: ({ numerador, denominador, resultado }) =>
        `${numerador}/${denominador} = ${resultado}%`,
    },
  },
  data() {
    return {
      expandidoNumerador: false,
      expandidoDenominador: false,
      limiteProcedimentos: 5,
      mountedAnimated: false,
      floatsNumerador: [],
      floatsDenominador: [],
      pulseNumerador: false,
      pulseDenominador: false,
      floatNumeradorId: 0,
      floatDenominadorId: 0,
      procNumeradorIndex: 0,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.mountedAnimated = true
    })
  },
  computed: {
    numeradorStep() {
      if (this.card == 'desenvolvimento-infantil') {
        return 20
      }

      if (this.card == 'gestantes-puerperas') {
        return 9
      }

      if (this.card == 'pessoa-diabetes') {
        return Number((20 + 15 + 20 + 15 + 15 + 15) / 6).toFixed(0)
      }

      if (this.card == 'pessoa-hipertensao') {
        return 25
      }

      if (this.card == 'pessoa-idosa') {
        return 25
      }

      if (this.card == 'saude-mulher') {
        return Number((20 + 30 + 30 + 20) / 4).toFixed(0)
      }

      return 1
    },

    resultadoCalculado() {
      return this.calculateResultFn({
        numerador: this.valorNumerador,
        denominador: this.valorDenominador,
      })
    },

    resultadoFormatado() {
      return `${this.resultadoCalculado}%`
    },

    classeCalculada() {
      return (
        this.calculateClassFn({
          numerador: this.valorNumerador,
          denominador: this.valorDenominador,
          resultado: this.resultadoCalculado,
        }) || { nivel: '-', variant: 'secondary' }
      )
    },

    deltaNumerador() {
      const diferenca =
        (Number(this.valorNumerador) || 0) - (Number(this.baseNumerador) || 0)

      if (this.card == 'desenvolvimento-infantil') {
        return diferenca / 20
      }

      if (this.card === 'gestantes-puerperas') {
        return Number((diferenca / 9).toFixed(0))
      }

      if (this.card == 'pessoa-diabetes') {
        let media = (20 + 15 + 20 + 15 + 15 + 15) / 6
        return Number((diferenca / media).toFixed(0))
      }

      if (this.card == 'pessoa-hipertensao') {
        return diferenca / 25
      }

      if (this.card == 'pessoa-idosa') {
        return diferenca / 25
      }

      if (this.card == 'saude-mulher') {
        let media = (20 + 30 + 30 + 20) / 4
        return Number((diferenca / media).toFixed(0))
      }

      return diferenca
    },

    deltaDenominador() {
      return (
        (Number(this.valorDenominador) || 0) -
        (Number(this.baseDenominador) || 0)
      )
    },

    gaugeStyle() {
      const value = Math.min(
        Math.max(Number(this.resultadoCalculado) || 0, 0),
        100
      )
      const colorMap = {
        Ótimo: '#444CE7',
        Bom: '#17B26A',
        Suficiente: '#F79009',
        Regular: '#F04438',
      }

      return {
        background: `conic-gradient(${
          colorMap[this.classeCalculada.nivel] || '#98A2B3'
        } 0 ${value}%, #F2F4F7 ${value}% 100%)`,
      }
    },

    resultPillClass() {
      return {
        'result-pill--otimo': this.classeCalculada.nivel === 'Ótimo',
        'result-pill--bom': this.classeCalculada.nivel === 'Bom',
        'result-pill--suficiente': this.classeCalculada.nivel === 'Suficiente',
        'result-pill--regular': this.classeCalculada.nivel === 'Regular',
      }
    },
  },

  methods: {
    /**
     * Abrir modal
     */
    open() {
      this.$refs.modal.show()
    },

    /**
     * Fechar modal
     */
    close() {
      this.$refs.modal.hide()
    },

    /**
     * Manipula a entrada manual do numerador, aplicando limites e efeitos visuais ao aumentar
     * @param {number|string} v
     * @returns {void}
     */
    onInputNumerador(v) {
      const atual = Number(this.valorNumerador) || 0
      const num = Math.max(
        this.numeradorMin,
        Math.min(this.maxValor, Number(v) || 0)
      )

      if (num > atual) {
        this.spawnProcedimentoNumerador()
        this.triggerPulseNumerador()
      }

      this.$emit('update:numerador', num)
    },

    /**
     * Manipula a entrada manual do denominador, aplicando limites e efeitos visuais ao aumentar
     * @param {number|string} v
     * @returns {void}
     */
    onInputDenominador(v) {
      const atual = Number(this.valorDenominador) || 0
      const num = Math.max(
        this.denominadorMin,
        Math.min(this.maxValor, Number(v) || 0)
      )

      if (num > atual) {
        this.spawnProcedimentoDenominador()
        this.triggerPulseDenominador()
      }

      this.$emit('update:denominador', num)
    },

    /**
     * Aplica um preset (Ótimo, Bom, etc.) delegando a lógica para uma função externa
     * @param {string} tipo
     * @returns {void}
     */
    onAplicarPreset(tipo) {
      this.applyPresetFn({
        tipo,
        numerador: this.valorNumerador,
        denominador: this.valorDenominador,
        setNumerador: (valor) => this.$emit('update:numerador', valor),
        setDenominador: (valor) => this.$emit('update:denominador', valor),
      })
    },

    /**
     * Alterna a exibição (expandido/recolhido) da lista de procedimentos do numerador ou denominador
     * @param {String} tipo
     * @returns {void}
     */
    toggleProcedimentos(tipo) {
      if (tipo === 'numerador') {
        this.expandidoNumerador = !this.expandidoNumerador
      } else {
        this.expandidoDenominador = !this.expandidoDenominador
      }
    },

    /**
     * Retorna a lista de procedimentos visíveis com base no estado expandido e no limite definido
     * @param {Array} items
     * @param {String} tipo
     * @returns {Array}
     */
    procedimentosVisiveis(items, tipo) {
      const expandido =
        tipo === 'numerador'
          ? this.expandidoNumerador
          : this.expandidoDenominador

      return expandido ? items : items.slice(0, this.limiteProcedimentos)
    },

    /**
     * Retorna as classes CSS do chip com base no tipo/classificação informado
     * @param {string} tipo
     * @returns {Object}
     */
    chipClass(tipo) {
      return {
        'chip--otimo': tipo === 'Ótimo',
        'chip--bom': tipo === 'Bom',
        'chip--suficiente': tipo === 'Suficiente',
        'chip--regular': tipo === 'Regular',
      }
    },

    /**
     * Calcula a posição percentual do slider com base no valor atual, mínimo e máximo
     * @param {number|string} value
     * @param {number} min
     * @returns {number}
     */
    sliderPercent(value, min) {
      const max = Number(this.maxValor) || 1
      if (max <= min) return 0
      return ((Number(value) - min) / (max - min)) * 100
    },

    /**
     * Dispara a animação de pulse do numerador
     * @returns {void}
     */
    triggerPulseNumerador() {
      this.pulseNumerador = false
      this.$nextTick(() => {
        this.pulseNumerador = true
        setTimeout(() => {
          this.pulseNumerador = false
        }, 450)
      })
    },

    /**
     * Dispara a animação de pulse do denominador
     * @returns {void}
     */
    triggerPulseDenominador() {
      this.pulseDenominador = false
      this.$nextTick(() => {
        this.pulseDenominador = true
        setTimeout(() => {
          this.pulseDenominador = false
        }, 450)
      })
    },

    /**
     * Cria um item flutuante visual para o numerador com base na lista de procedimentos
     * @returns {void}
     */
    spawnProcedimentoNumerador() {
      if (!this.procedimentosNumerador.length) return

      const proc =
        this.procedimentosNumerador[
          this.procNumeradorIndex % this.procedimentosNumerador.length
        ]

      this.procNumeradorIndex += 1
      this.floatNumeradorId += 1

      const item = {
        id: this.floatNumeradorId,
        proc,
        offsetX: Math.round(Math.random() * 40 - 20),
      }

      this.floatsNumerador = [...this.floatsNumerador.slice(-5), item]

      setTimeout(() => {
        this.floatsNumerador = this.floatsNumerador.filter(
          (x) => x.id !== item.id
        )
      }, 2200)
    },

    /**
     * Cria um item flutuante visual para o denominador com base na lista de procedimentos
     * @returns {void}
     */
    spawnProcedimentoDenominador() {
      if (!this.procedimentosDenominador.length) return

      const index = Math.floor(
        Math.random() * this.procedimentosDenominador.length
      )
      const proc = this.procedimentosDenominador[index]

      this.floatDenominadorId += 1

      const item = {
        id: this.floatDenominadorId,
        proc,
        offsetX: Math.round(Math.random() * 40 - 20),
      }

      this.floatsDenominador = [...this.floatsDenominador.slice(-5), item]

      setTimeout(() => {
        this.floatsDenominador = this.floatsDenominador.filter(
          (x) => x.id !== item.id
        )
      }, 2200)
    },
  },
}
</script>

<style>
.btn-simulador {
  text-transform: inherit;
}
</style>

<style scoped lang="scss">
.sim-page-root {
  min-height: 100%;
  background: linear-gradient(160deg, #f8f9fc 0%, #eef0f8 40%, #f5f3ff 100%);
  padding: 8px;
}

.sim-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 8px;
}

.controls-row {
  display: grid;
  grid-template-columns: 1fr 1fr 220px;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 20px;
}

.slider-card,
.result-card,
.section-card,
.proc-table {
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.slider-card {
  padding: 20px 22px 18px;
  position: relative;
  overflow: visible;
  transition: box-shadow 0.3s ease, transform 0.2s ease;
}

.slider-card:hover {
  box-shadow: 0 4px 20px rgba(68, 76, 231, 0.06);
}

.slider-pulse {
  animation: cardPulse 0.45s ease;
}

@keyframes cardPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(68, 76, 231, 0.2);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(68, 76, 231, 0.04);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(68, 76, 231, 0);
  }
}

.result-card {
  transition: box-shadow 0.3s ease;
}

.result-card:hover {
  box-shadow: 0 4px 20px rgba(68, 76, 231, 0.08);
}

.chip {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.chip:hover {
  transform: translateY(-2px);
  filter: brightness(0.98);
}

.chip-active {
  transform: translateY(-1px);
}

/* entrada */
.fade-up {
  animation: fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
}

.fade-up-1 {
  animation-delay: 0.08s;
}

.fade-up-2 {
  animation-delay: 0.16s;
}

.fade-up-3 {
  animation-delay: 0.24s;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* floating procedures */
.float-anchor {
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  height: 0;
  pointer-events: none;
  z-index: 50;
  overflow: visible;
}

.float-proc {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #fff 60%, #eef4ff 100%);
  border: 1.5px solid #c7d7fe;
  border-radius: 16px;
  padding: 12px 18px;
  white-space: nowrap;
  box-shadow: 0 10px 40px rgba(68, 76, 231, 0.18), 0 2px 8px rgba(0, 0, 0, 0.05);
  pointer-events: none;
}

.float-proc--denom {
  background: linear-gradient(135deg, #fff 60%, #ecfdf3 100%);
  border-color: #abefc6;
  box-shadow: 0 10px 40px rgba(23, 178, 106, 0.15),
    0 2px 8px rgba(0, 0, 0, 0.05);
}

.float-proc-icon {
  font-size: 22px;
  animation: toothWiggle 0.5s ease 0.1s;
}

@keyframes toothWiggle {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  20% {
    transform: rotate(-14deg) scale(1.1);
  }
  60% {
    transform: rotate(10deg) scale(1.05);
  }
}

.float-proc-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.float-proc-code {
  font-size: 11.5px;
  font-weight: 700;
  color: #444ce7;
  font-family: monospace;
  letter-spacing: 0.03em;
}

.float-proc-code--denom {
  color: #067647;
}

.float-proc-desc {
  font-size: 11px;
  color: #475467;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.float-proc-check {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: #17b26a;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(23, 178, 106, 0.3);
  animation: checkPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
}

.float-proc-check--denom {
  background: #067647;
}

@keyframes checkPop {
  0% {
    transform: scale(0) rotate(-30deg);
    opacity: 0;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.float-proc-enter-active {
  animation: floatUp 2.4s cubic-bezier(0.22, 0.68, 0.36, 1) forwards;
}

.float-proc-leave-active {
  animation: none;
}

.float-proc-enter,
.float-proc-leave-to {
  opacity: 0;
}

@keyframes floatUp {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(8px) scale(0.85);
  }
  10% {
    opacity: 1;
    transform: translateX(calc(-50% + var(--ox, 0px))) translateY(-4px)
      scale(1.02);
  }
  15% {
    transform: translateX(calc(-50% + var(--ox, 0px))) translateY(-8px) scale(1);
  }
  70% {
    opacity: 1;
    transform: translateX(calc(-50% + var(--ox, 0px))) translateY(-70px)
      scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(calc(-50% + var(--ox, 0px))) translateY(-100px)
      scale(0.8);
  }
}

.slider-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.slider-card__title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-card__icon {
  background: #2e70d9;
  color: #fff;
  font-size: 12px;
  border-radius: 6px;
  width: 27px;
  height: 27px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slider-card__icon.red {
  background: red;
}

.slider-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  letter-spacing: 0.02em;
}

.delta-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  font-family: monospace;
}

.delta-badge--pos {
  color: #067647;
  background: #ecfdf3;
}

.delta-badge--neg {
  color: #b42318;
  background: #fef3f2;
}

.slider-wrap {
  position: relative;
  margin-bottom: 12px;
  height: 24px;
  display: flex;
  align-items: center;
}

.slider-track {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 6px;
  border-radius: 999px;
  background: #f2f4f7;
  transform: translateY(-50%);
  z-index: 1;
}

.slider-fill {
  position: absolute;
  top: 50%;
  left: 0;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, #444ce7, #7a5af8);
  transform: translateY(-50%);
  z-index: 2;
  transition: width 0.15s ease;
}

.slider-input {
  position: relative;
  z-index: 3;
  width: 100%;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
}

.slider-input::-webkit-slider-runnable-track {
  height: 6px;
  background: transparent;
}

.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #444ce7;
  box-shadow: 0 2px 8px rgba(68, 76, 231, 0.25);
  cursor: pointer;
  margin-top: -8px;
}

.slider-input::-moz-range-track {
  height: 6px;
  background: transparent;
}

.slider-input::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #444ce7;
  box-shadow: 0 2px 8px rgba(68, 76, 231, 0.25);
  cursor: pointer;
}

.number-input {
  width: 100%;
  text-align: center;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 600;
  color: #1d2939;
}

.number-input:focus {
  border-color: #444ce7;
  box-shadow: none;
}

.result-card {
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 220px;
}

.gauge-ring {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  margin-bottom: 12px;
}

.gauge-ring__inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-ring__value {
  font-size: 24px;
  font-weight: 700;
  color: #344054;
  line-height: 1;
}

.gauge-ring__label {
  font-size: 11px;
  font-weight: 700;
  margin-top: 6px;
  color: #667085;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.result-pill {
  margin-top: 6px;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid transparent;
  text-align: center;
}

.result-pill--otimo {
  background: #eef4ff;
  border-color: #c7d7fe;
  color: #3538cd;
}

.result-pill--bom {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.result-pill--suficiente {
  background: #fffaeb;
  border-color: #fedf89;
  color: #b54708;
}

.result-pill--regular {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.section-card {
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #667085;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: transparent;
  padding: 0;
}

.chips-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.chip {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 2px solid transparent;
  padding: 16px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.chip:hover {
  transform: translateY(-2px);
}

.chip-active {
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.06);
}

.chip__title {
  font-weight: 700;
  font-size: 14px;
}

.chip__sub {
  font-size: 11px;
  opacity: 0.75;
  margin-top: 2px;
}

.chip--otimo {
  background: #eef4ff;
  border-color: #c7d7fe;
  color: #3538cd;
}

.chip--bom {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.chip--suficiente {
  background: #fffaeb;
  border-color: #fedf89;
  color: #b54708;
}

.chip--regular {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.section-hint {
  font-size: 12px;
  color: #98a2b3;
  margin-top: 12px;
  margin-bottom: 0;
  line-height: 1.5;
}

.procedures-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.proc-table {
  overflow: hidden;
}

.proc-header {
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #f2f4f7;
  background: linear-gradient(135deg, #f9fafb 0%, #fff 100%);
}

.proc-header__icon {
  background: #2e70d9;
  color: #fff;
  font-size: 12px;
  border-radius: 6px;
  width: 27px;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.proc-header__icon.red {
  background: red;
}

.proc-header__title {
  font-weight: 700;
  font-size: 13px;
  color: #1d2939;
  letter-spacing: 0.02em;
}

.proc-header__count {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  background: #f2f4f7;
  padding: 2px 8px;
  border-radius: 20px;
}

.proc-body {
  padding: 4px 0;
  max-height: 420px;
  overflow-y: auto;
}

.proc-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 20px;
  border-bottom: 1px solid #f9fafb;
}

.proc-row:last-child {
  border-bottom: none;
}

.proc-code {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 6px;
  white-space: nowrap;
  font-family: monospace;
  font-weight: 600;
  flex-shrink: 0;
}

.proc-code--red {
  color: red;
  background: rgba(180, 35, 24, 0.08);
}

.proc-code--blue {
  color: #2e70d9;
  background: rgba(68, 76, 231, 0.08);
}

.proc-desc {
  font-size: 13px;
  color: #475467;
  line-height: 1.4;
}

.proc-toggle {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 14px 20px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #2e70d9;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
}

.proc-toggle:hover {
  background: #f9fafb;
}

.sim-footer {
  text-align: center;
  padding: 16px 0 4px;
  font-size: 12px;
  color: #98a2b3;
}

@media (max-width: 991px) {
  .controls-row {
    grid-template-columns: 1fr;
  }

  .procedures-grid {
    grid-template-columns: 1fr;
  }

  .chips-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 576px) {
  .chips-row {
    grid-template-columns: 1fr;
  }
}
</style>
